import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useRole } from "../../components/AppShell";
import { WhatsAppIcon } from "../../components/icons";
import { TabNav } from "../../components/TabNav";
import { Avatar, Empty, useConfirm, useToast } from "../../components/ui";
import { ApiError, api } from "../../lib/api";
import { ROLE_LABEL, shortTime, TEMPLATE_CATEGORY_LABEL, TEMPLATE_STATUS_LABEL } from "../../lib/format";
import {
  invitationsQuery,
  keys,
  useMe,
  useQuickReplies,
  useSyncTemplates,
  useTeam,
  useTemplates,
  useUpdateTemplate,
} from "../../lib/queries";
import type { QuickReply, Role, Template } from "../../lib/types";
import { RoleSummary, RolesTable } from "./Roles";
import { SupportAccessCard } from "./SupportAccess";
import { AreasCard, VisibilityCard } from "./TeamAreas";
import { TemplateEditorDialog } from "./TemplateEditor";
import { TemplateVariablesDialog, TemplateVariablesHelp } from "./TemplateVariables";

/**
 * How the company works in Orbis: who is on the team and its WhatsApp (templates and
 * quick replies). Lumi, the catalog, the knowledge and the company have their own sections.
 */
export function SettingsPage() {
  const { seccion } = useSearch({ from: "/app/ajustes" });
  const role = useRole();
  const canManage = role === "owner" || role === "admin";
  return (
    <div className="page">
      <div className="page-head">
        <h1>Ajustes</h1>
      </div>
      <TabNav label="Ajustes">
        <Link
          to="/ajustes"
          search={{}}
          activeOptions={{ exact: true, includeSearch: true }}
          aria-current={seccion ? undefined : "page"}
        >
          Equipo
        </Link>
        <Link
          to="/ajustes"
          search={{ seccion: "whatsapp" }}
          activeOptions={{ exact: true, includeSearch: true }}
          aria-current={seccion === "whatsapp" ? "page" : undefined}
        >
          WhatsApp
        </Link>
      </TabNav>
      {seccion === "whatsapp" ? (
        <>
          <TemplatesCard />
          <QuickRepliesCard />
        </>
      ) : (
        <>
          <TeamCard />
          <VisibilityCard canManage={canManage} />
          <AreasCard canManage={canManage} />
          {role === "owner" && <SupportAccessCard />}
        </>
      )}
    </div>
  );
}

function QuickRepliesCard() {
  const role = useRole();
  const canManage = role === "owner" || role === "admin";
  const replies = useQuickReplies();
  const qc = useQueryClient();
  const toast = useToast();
  const dialog = useRef<HTMLDialogElement>(null);
  const [editing, setEditing] = useState<QuickReply | null>(null);
  // A new key per "Nueva respuesta" so the form never reopens with the last one's text.
  const [formKey, setFormKey] = useState(0);
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: Record<string, string | null>) =>
      editing ? api.patch(`/quick-replies/${editing.id}`, body) : api.post("/quick-replies", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.quickReplies }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/quick-replies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.quickReplies }),
  });

  // Losing the permission while the editor is open (role changed live) closes it.
  useEffect(() => {
    if (!canManage && dialog.current?.open) {
      dialog.current.close();
      toast("Tu rol cambió: ya no puedes editar respuestas rápidas.");
    }
  }, [canManage, toast]);

  function open(reply: QuickReply | null) {
    setEditing(reply);
    setFormKey((k) => k + 1);
    setError(null);
    dialog.current?.showModal();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await save.mutateAsync({
        shortcut: String(f.get("shortcut")),
        title: String(f.get("title")),
        body: String(f.get("body")),
        category: String(f.get("category") ?? "").trim() || null,
      });
      dialog.current?.close();
      toast(editing ? "Respuesta actualizada" : "Respuesta creada");
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "conflict"
          ? "Ese atajo ya existe. Usa otro."
          : err instanceof ApiError
            ? err.message
            : "No se pudo guardar.",
      );
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="page-head">
          <div>
            <h3>Respuestas rápidas</h3>
            <p className="muted">En la bandeja, escribe / y el atajo para insertarlas.</p>
          </div>
          {canManage && (
            <button className="btn btn-primary" type="button" onClick={() => open(null)}>
              Nueva respuesta
            </button>
          )}
        </div>
        {replies.data?.length === 0 ? (
          <Empty title="Todavía no hay respuestas rápidas.">
            <span>Sirven para lo que el equipo escribe una y otra vez: horarios, garantías, cuentas bancarias.</span>
          </Empty>
        ) : (
          <ul className="entity-list flush">
            {replies.data?.map((r) => (
              <li key={r.id} className="entity-row no-avatar">
                <span className="entity-main">
                  <span className="entity-name">
                    <span className="pill pill-closed mono">/{r.shortcut}</span> {r.title}
                  </span>
                  <span className="entity-text">{r.body}</span>
                </span>
                {canManage && (
                  <span className="entity-flags">
                    <button className="btn btn-sm" type="button" onClick={() => open(r)}>
                      Editar
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      type="button"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `¿Eliminar la respuesta /${r.shortcut}?`,
                          message: "Se borra para todo el equipo y no se puede deshacer.",
                          confirmLabel: "Eliminar",
                        });
                        if (ok) remove.mutateAsync(r.id).then(() => toast("Respuesta eliminada"));
                      }}
                    >
                      Eliminar
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <dialog ref={dialog} aria-labelledby="qr-title">
        <form className="dialog-form" key={`${editing?.id ?? "new"}-${formKey}`} onSubmit={onSubmit}>
          <h3 id="qr-title">{editing ? "Editar respuesta" : "Nueva respuesta rápida"}</h3>
          <div className="field">
            <label htmlFor="qr-shortcut">Atajo</label>
            <input
              className="input"
              id="qr-shortcut"
              name="shortcut"
              required
              placeholder="garantia"
              defaultValue={editing?.shortcut}
            />
          </div>
          <div className="field">
            <label htmlFor="qr-title">Título</label>
            <input
              className="input"
              id="qr-title-input"
              placeholder="Ej.: Proceso de garantía"
              name="title"
              required
              maxLength={80}
              defaultValue={editing?.title}
            />
          </div>
          <div className="field">
            <label htmlFor="qr-body">Texto</label>
            <textarea
              className="textarea"
              id="qr-body"
              placeholder="Ej.: Para tramitar la garantía necesitamos la factura y una foto del producto."
              name="body"
              required
              maxLength={4096}
              rows={5}
              defaultValue={editing?.body}
            />
          </div>
          <div className="field">
            <label htmlFor="qr-category">Categoría (opcional)</label>
            <input
              className="input"
              id="qr-category"
              placeholder="Ej.: Garantías"
              name="category"
              maxLength={40}
              defaultValue={editing?.category ?? ""}
            />
          </div>
          {error && <p className="error-text">{error}</p>}
          <div className="row">
            <button className="btn" type="button" onClick={() => dialog.current?.close()}>
              Cancelar
            </button>
            <button className="btn btn-primary" type="submit" disabled={save.isPending}>
              Guardar
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}

function TeamCard() {
  const role = useRole();
  const canManage = role === "owner" || role === "admin";
  const isOwner = role === "owner";
  const confirm = useConfirm();
  const team = useTeam();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const invitations = useQuery({ ...invitationsQuery, enabled: canManage });
  const [link, setLink] = useState<{ email: string; url: string; emailSent: boolean } | null>(null);
  const [inviteRole, setInviteRole] = useState<Role>("agent");
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: keys.team });
    qc.invalidateQueries({ queryKey: ["invitations"] });
  };
  const fail = (err: unknown, fallback: string) => toast(err instanceof ApiError ? err.message : fallback);

  async function onInvite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setError(null);
    try {
      const created = await api.post<{ email: string; url: string; emailSent: boolean }>("/team/invitations", {
        email: String(f.get("email")),
        role: String(f.get("role")),
      });
      setLink({ email: created.email, url: created.url, emailSent: created.emailSent });
      if (created.emailSent) toast(`Enviamos la invitación a ${created.email}`);
      form.reset();
      refresh();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "conflict"
          ? "Esa persona ya es parte del equipo."
          : "No se pudo crear la invitación.",
      );
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <div>
          <h3>Equipo</h3>
          <p className="muted">
            Invita por correo: Orbis envía un enlace de un solo uso que vence en 7 días. También puedes copiarlo y
            mandarlo por WhatsApp.
          </p>
        </div>
        <RolesTable />

        {canManage && (
          <form className="invite-form" onSubmit={onInvite}>
            <input
              className="input"
              name="email"
              type="email"
              required
              placeholder="correo@empresa.com"
              aria-label="Correo"
            />
            <select
              className="select"
              name="role"
              aria-label="Rol"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Role)}
            >
              <option value="agent">{ROLE_LABEL.agent}</option>
              <option value="admin">{ROLE_LABEL.admin}</option>
              {isOwner && <option value="owner">{ROLE_LABEL.owner}</option>}
            </select>
            <button className="btn btn-primary" type="submit">
              Crear invitación
            </button>
          </form>
        )}
        {canManage && <RoleSummary role={inviteRole} />}
        {error && <p className="error-text">{error}</p>}
        {link && (
          <div className="banner banner-info invite-link">
            <span>
              {link.emailSent ? (
                <>
                  Le enviamos el enlace a <strong>{link.email}</strong>. Si no le llega, cópialo y mándalo tú (solo se
                  muestra ahora):
                </>
              ) : (
                <>
                  Enlace para <strong>{link.email}</strong>. Solo se muestra ahora:
                </>
              )}
            </span>
            <code className="mono">{link.url}</code>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() =>
                navigator.clipboard
                  .writeText(link.url)
                  .then(() => toast("Enlace copiado"))
                  .catch(() => toast("Selecciona el enlace y cópialo"))
              }
            >
              Copiar enlace
            </button>
          </div>
        )}

        <ul className="entity-list flush">
          {team.data?.items.map((m) => (
            <li key={m.id} className="entity-row">
              <Avatar name={m.name} fallback={m.email} />
              <span className="entity-main">
                <span className="entity-name">
                  {m.name}
                  {m.id === me.data?.user.id && <span className="faint"> (tú)</span>}
                </span>
                <span className="entity-meta">{m.email}</span>
              </span>
              <span className="entity-flags">
                {isOwner ? (
                  <select
                    className="select btn-sm"
                    aria-label={`Rol de ${m.name}`}
                    value={m.role}
                    onChange={(e) =>
                      api
                        .patch(`/team/${m.id}`, { role: e.target.value })
                        .then(() => toast("Rol actualizado"))
                        .catch((err) => fail(err, "No se pudo cambiar el rol."))
                        .finally(refresh)
                    }
                  >
                    {Object.entries(ROLE_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="pill pill-closed">{ROLE_LABEL[m.role]}</span>
                )}
                {canManage && m.id !== me.data?.user.id && (isOwner || m.role !== "owner") && (
                  <button
                    className="btn btn-sm btn-danger"
                    type="button"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `¿Quitar a ${m.name} del equipo?`,
                        message:
                          "Pierde el acceso a esta empresa de inmediato y sus conversaciones quedan sin asignar. Para volver a entrar necesitará una invitación nueva.",
                        confirmLabel: "Quitar",
                      });
                      if (!ok) return;
                      api
                        .delete(`/team/${m.id}`)
                        .then(() => toast(`${m.name} ya no es parte del equipo`))
                        .catch((err) => fail(err, "No se pudo quitar."))
                        .finally(refresh);
                    }}
                  >
                    Quitar
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>

        {canManage && (invitations.data?.length ?? 0) > 0 && (
          <div className="section">
            <h3>Invitaciones pendientes</h3>
            <ul className="entity-list flush">
              {invitations.data?.map((inv) => (
                <li key={inv.id} className="entity-row no-avatar">
                  <span className="entity-main">
                    <span className="entity-name">{inv.email}</span>
                    <span className="entity-meta">
                      <span>{ROLE_LABEL[inv.role]}</span>
                      <span>{inv.state === "expired" ? "Vencida" : `Vence ${shortTime(inv.expiresAt)}`}</span>
                    </span>
                  </span>
                  <span className="entity-flags">
                    <button
                      className="btn btn-sm btn-danger"
                      type="button"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `¿Cancelar la invitación a ${inv.email}?`,
                          message: "El enlace que recibió deja de funcionar.",
                          confirmLabel: "Cancelar invitación",
                          cancelLabel: "Volver",
                        });
                        if (!ok) return;
                        api
                          .delete(`/team/invitations/${inv.id}`)
                          .then(() => toast("Invitación cancelada"))
                          .catch((err) => fail(err, "No se pudo cancelar."))
                          .finally(refresh);
                      }}
                    >
                      Cancelar
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_PILL: Record<string, string> = { APPROVED: "pill-human", PENDING: "pill-warn", REJECTED: "pill-closed" };

function TemplatesCard() {
  const role = useRole();
  const canSync = role === "owner" || role === "admin";
  const templates = useTemplates(false);
  const sync = useSyncTemplates();
  const setHidden = useUpdateTemplate();
  const [editingVars, setEditingVars] = useState<Template | null>(null);
  const [creating, setCreating] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const pending = templates.data?.some((t) => t.status === "PENDING") ?? false;
  // While Meta reviews a template, ask it again every 30 s so the result shows up alone.
  useEffect(() => {
    if (!pending || !canSync) return;
    const timer = setInterval(() => sync.mutate(), 30_000);
    return () => clearInterval(timer);
  }, [pending, canSync, sync.mutate]);

  async function remove(t: Template) {
    const ok = await confirm({
      title: `¿Eliminar la plantilla ${t.name}?`,
      message: "Se borra también en Meta y no se puede deshacer. Meta no deja usar el mismo nombre por 30 días.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await api.delete(`/templates/${t.id}`);
      qc.invalidateQueries({ queryKey: ["templates"] });
      toast(`${t.name} eliminada`);
    } catch (err) {
      const meta = err instanceof ApiError ? err.details?.meta : undefined;
      toast(meta ? `Meta no la eliminó: «${String(meta)}»` : "No se pudo eliminar.");
    }
  }
  const [showHidden, setShowHidden] = useState(false);
  const hiddenCount = templates.data?.filter((t) => t.hidden).length ?? 0;
  const rows = templates.data?.filter((t) => showHidden || !t.hidden) ?? [];

  function toggleHidden(id: string, hidden: boolean, name: string) {
    setHidden
      .mutateAsync({ id, hidden })
      .then(() => toast(hidden ? `${name} ya no aparece en el chat` : `${name} vuelve a aparecer`))
      .catch((err) => toast(err instanceof ApiError ? err.message : "No se pudo cambiar."));
  }

  async function onSync() {
    try {
      const { results } = await sync.mutateAsync();
      const failed = results.filter((r) => r.error);
      const total = results.reduce((n, r) => n + (r.synced ?? 0), 0);
      toast(
        failed.length
          ? "Meta no devolvió las plantillas de un número. Revisa su token."
          : `${total} plantillas sincronizadas`,
      );
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo sincronizar.");
    }
  }

  const lastSync = templates.data?.reduce<string | null>(
    (max, t) => (!max || t.syncedAt > max ? t.syncedAt : max),
    null,
  );

  return (
    <div className="card">
      <div className="card-body">
        <div className="page-head">
          <div>
            <h3 className="with-icon">
              <WhatsAppIcon />
              Plantillas de WhatsApp
            </h3>
            <p className="muted">
              Sirven para escribirle a un cliente después de 24 horas. Créalas aquí y Meta las revisa, casi siempre en
              minutos. Las que ya tengas en el{" "}
              <a href="https://business.facebook.com/wa/manage/message-templates/" target="_blank" rel="noreferrer">
                administrador de WhatsApp de Meta
              </a>{" "}
              llegan al sincronizar.{lastSync ? ` Última sincronización: ${shortTime(lastSync)}` : ""}
            </p>
          </div>
          {canSync && (
            <div className="row">
              <button className="btn" type="button" onClick={onSync} disabled={sync.isPending}>
                {sync.isPending ? "Sincronizando…" : "Sincronizar con Meta"}
              </button>
              <button className="btn btn-primary" type="button" onClick={() => setCreating(true)}>
                Nueva plantilla
              </button>
            </div>
          )}
        </div>
        <TemplateVariablesHelp />
        {templates.data?.length === 0 ? (
          <Empty title="Todavía no hay plantillas sincronizadas." />
        ) : (
          <ul className="entity-list flush">
            {rows.map((t) => (
              <li key={t.id} className={`entity-row no-avatar${t.hidden ? " row-hidden" : ""}`}>
                <span className="entity-main">
                  <span className="entity-name mono">{t.name}</span>
                  <span className="entity-meta">
                    <span>{t.language}</span>
                    <span>{TEMPLATE_CATEGORY_LABEL[t.category] ?? t.category}</span>
                    <span className={`pill ${STATUS_PILL[t.status] ?? "pill-closed"}`}>
                      {TEMPLATE_STATUS_LABEL[t.status] ?? t.status}
                    </span>
                    {!t.supported && <span className="pill pill-warn">Con imagen: pronto</span>}
                    {t.status === "REJECTED" && t.rejectedReason && (
                      <span className="faint">Motivo de Meta: {t.rejectedReason}</span>
                    )}
                    {t.hidden && <span className="pill pill-closed">Oculta</span>}
                  </span>
                  <span className="entity-text">{t.bodyText}</span>
                </span>
                {canSync && (
                  <span className="entity-flags">
                    {t.bodyVariables.length + t.headerVariables.length > 0 && (
                      <button className="btn btn-sm" type="button" onClick={() => setEditingVars(t)}>
                        Variables
                      </button>
                    )}
                    <button
                      className="btn btn-sm"
                      type="button"
                      disabled={setHidden.isPending}
                      onClick={() => toggleHidden(t.id, !t.hidden, t.name)}
                    >
                      {t.hidden ? "Mostrar" : "Ocultar"}
                    </button>
                    <button className="btn btn-sm btn-ghost" type="button" onClick={() => remove(t)}>
                      Eliminar
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {editingVars && <TemplateVariablesDialog template={editingVars} onClose={() => setEditingVars(null)} />}
        {creating && <TemplateEditorDialog onClose={() => setCreating(false)} />}
        {hiddenCount > 0 && (
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => setShowHidden((v) => !v)}>
            {showHidden ? "Esconder las ocultas" : `Ver ocultas (${hiddenCount})`}
          </button>
        )}
      </div>
    </div>
  );
}
